import { useState, useEffect, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { AssetTypePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

const CATEGORIES = [
  { value: "barrier", label: "Speed Barrier" },
  { value: "dugout", label: "Dugout" },
  { value: "pavilion", label: "Pavilion" },
  { value: "jersey", label: "Jersey" },
  { value: "other", label: "Other" },
];

export default function AssetTypeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [type, setType] = useState<AssetTypePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("barrier");
  const [widthMm, setWidthMm] = useState("");
  const [heightMm, setHeightMm] = useState("");
  const [depthMm, setDepthMm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getAssetType(id)
      .then((res) => {
        const t = res.asset_type;
        setType(t);
        setName(t.name);
        setDescription(t.description ?? "");
        setCategory(t.category);
        setWidthMm(String(t.width_mm));
        setHeightMm(String(t.height_mm));
        setDepthMm(t.depth_mm != null ? String(t.depth_mm) : "");
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load asset type"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.updateAssetType(id, {
        name,
        description: description || null,
        category: category as "barrier" | "dugout" | "pavilion" | "jersey" | "other",
        width_mm: parseInt(widthMm, 10),
        height_mm: parseInt(heightMm, 10),
        depth_mm: depthMm ? parseInt(depthMm, 10) : null,
      });
      navigate(`/asset-types/${id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to update asset type");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="asset-type-edit" data-state="loading">Loading...</p>;
  if (error && !type) return <p className="text-sm text-destructive" data-page="asset-type-edit" data-state="error" data-error>{error}</p>;
  if (!type) return <p className="text-muted-foreground" data-page="asset-type-edit" data-state="empty">Asset type not found</p>;

  const selectClass =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="flex flex-col gap-6" data-page="asset-type-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit Asset Type</h1>
        <p className="text-muted-foreground">{type.name}</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Asset Type Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="asset-type-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-field="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                data-field="description"
                placeholder="Optional description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                data-field="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={selectClass}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="widthMm">Width (mm)</Label>
                <Input
                  id="widthMm"
                  data-field="width-mm"
                  type="number"
                  min="1"
                  value={widthMm}
                  onChange={(e) => setWidthMm(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="heightMm">Height (mm)</Label>
                <Input
                  id="heightMm"
                  data-field="height-mm"
                  type="number"
                  min="1"
                  value={heightMm}
                  onChange={(e) => setHeightMm(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="depthMm">Depth (mm, optional)</Label>
              <Input
                id="depthMm"
                data-field="depth-mm"
                type="number"
                min="1"
                value={depthMm}
                onChange={(e) => setDepthMm(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link to={`/asset-types/${id}`} className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
